import { useEffect, useMemo, useState } from 'react';
import type { InvoicePaymentMethod, InvoicePaymentMethodId } from '@repo/api-client/dtos';
import { useStripeConnectStatus } from '@repo/queries';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { defaultPaymentMethodLabel } from './invoiceFormSchema';

const BASE_ORDERED_IDS: InvoicePaymentMethodId[] = [
  'paypal',
  'bank-transfer',
  'cash',
  'check',
  'custom',
];

const DETAIL_HINTS: Partial<Record<InvoicePaymentMethodId, string>> = {
  paypal: 'PayPal handle or email',
  'bank-transfer': 'Account number, routing, IBAN…',
  cash: 'Optional — pickup location, etc.',
  check: 'Optional — payable-to name',
  custom: 'Describe how to pay',
};

const NO_DETAIL_IDS = new Set<InvoicePaymentMethodId>(['stripe-checkout']);

interface PaymentMethodsDialogProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  initial: InvoicePaymentMethod[];
  onSave: (next: InvoicePaymentMethod[]) => void;
}

interface DraftState {
  selected: boolean;
  label: string;
  detail: string;
}

export function PaymentMethodsDialog({
  open,
  onOpenChange,
  initial,
  onSave,
}: PaymentMethodsDialogProps) {
  const { data: stripeStatus } = useStripeConnectStatus();
  const orderedIds = useMemo(() => {
    if (stripeStatus?.chargesEnabled) {
      return ['stripe-checkout' as const, ...BASE_ORDERED_IDS];
    }
    return BASE_ORDERED_IDS;
  }, [stripeStatus?.chargesEnabled]);

  const [drafts, setDrafts] = useState<Record<InvoicePaymentMethodId, DraftState>>(() =>
    seed(initial, orderedIds),
  );

  useEffect(() => {
    if (open) setDrafts(seed(initial, orderedIds));
  }, [open, initial, orderedIds]);

  function patch(id: InvoicePaymentMethodId, next: Partial<DraftState>) {
    setDrafts((prev) => ({ ...prev, [id]: { ...prev[id], ...next } }));
  }

  function handleSave() {
    const result: InvoicePaymentMethod[] = orderedIds.flatMap((id) => {
      const d = drafts[id];
      if (!d?.selected) return [];
      const label = d.label.trim() || defaultPaymentMethodLabel(id);
      const detail = d.detail.trim();
      if (NO_DETAIL_IDS.has(id)) {
        return [{ id, label }];
      }
      return [{ id, label, ...(detail ? { detail } : {}) }];
    });
    onSave(result);
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Payment methods</DialogTitle>
          <DialogDescription>
            Pick the ways your client can pay. These appear in the invoice footer
            and on the public link.
          </DialogDescription>
        </DialogHeader>

        <div className="flex max-h-[55vh] flex-col gap-3 overflow-y-auto pr-1">
          {orderedIds.map((id) => {
            const d = drafts[id];
            const inputId = `pm-${id}`;
            const showDetail = d.selected && !NO_DETAIL_IDS.has(id);
            return (
              <div
                key={id}
                className="flex flex-col gap-2 rounded-lg border border-line bg-secondary/20 p-3"
              >
                <label
                  htmlFor={inputId}
                  className="flex items-center gap-2 text-sm font-medium text-foreground"
                >
                  <Checkbox
                    id={inputId}
                    checked={d.selected}
                    onCheckedChange={(v) => patch(id, { selected: v === true })}
                  />
                  {defaultPaymentMethodLabel(id)}
                </label>
                {id === 'stripe-checkout' && d.selected ? (
                  <p className="ml-7 text-xs text-muted-foreground">
                    Clients can pay the full balance from your public invoice link.
                  </p>
                ) : null}
                {showDetail ? (
                  <div className="ml-7 flex flex-col gap-2">
                    <Label
                      htmlFor={`${inputId}-detail`}
                      className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground"
                    >
                      Detail
                    </Label>
                    <Textarea
                      id={`${inputId}-detail`}
                      value={d.detail}
                      onChange={(e) => patch(id, { detail: e.target.value })}
                      placeholder={DETAIL_HINTS[id]}
                      rows={2}
                      className="min-h-[56px] resize-y"
                    />
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave}>Save methods</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function seed(
  initial: InvoicePaymentMethod[],
  orderedIds: readonly InvoicePaymentMethodId[],
): Record<InvoicePaymentMethodId, DraftState> {
  const byId = new Map(initial.map((m) => [m.id, m]));
  const out = {} as Record<InvoicePaymentMethodId, DraftState>;
  for (const id of orderedIds) {
    const existing = byId.get(id);
    out[id] = {
      selected: !!existing,
      label: existing?.label ?? defaultPaymentMethodLabel(id),
      detail: existing?.detail ?? '',
    };
  }
  out.none = { selected: false, label: defaultPaymentMethodLabel('none'), detail: '' };
  if (!out['stripe-checkout']) {
    out['stripe-checkout'] = {
      selected: false,
      label: defaultPaymentMethodLabel('stripe-checkout'),
      detail: '',
    };
  }
  return out;
}
